import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
} from "react";

import { useGetFreeSpinGames } from "../../../../queries/free-spins/useGetFreeSpinGames";
import type { FreeSpinGame } from "../../../../queries/free-spins/useGetFreeSpinGames";
import { useDebouncedSearchQuery } from "../../../../hooks/useDebouncedSearchQuery";
import type { FormInstance } from "antd";
import {
  rememberRestrictionDisplayName,
  RESTRICTION_DISPLAY_NAMES_PARAM_KEY,
  usableRestrictionDisplayName,
} from "./restrictionDisplayNames";

import styles from "./FreeSpinSelect.module.css";

const PAGE_SIZE = 20;

type GameChip = { id: string; label: string };

const parseCSV = (csv: string | undefined): string[] => {
  if (!csv) return [];
  return csv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const toCSV = (ids: string[]): string => ids.join(",");

const gameLabel = (game: FreeSpinGame): string =>
  `${game.game_name} (${game.game_id})`;

const chipLabelToName = (id: string, label: string): string | undefined => {
  const suffix = ` (${id})`;
  const name =
    label.endsWith(suffix) ? label.slice(0, -suffix.length) : label;
  return usableRestrictionDisplayName(id, name);
};

const buildRestrictionLabels = (
  ids: string[],
  cache: Record<string, string>,
): Record<string, string> => {
  const labels: Record<string, string> = {};
  for (const id of ids) {
    const label = cache[id];
    if (!label) continue;
    const name = chipLabelToName(id, label);
    if (name) labels[id] = name;
  }
  return labels;
};

export type FreeSpinGameSelectProps = {
  value?: string;
  onChange?: (value: string) => void;
  /** Current provider — scopes the game list. */
  provider?: string;
  disabled?: boolean;
  /** Mirrors chip labels onto the step for banner deeplink display names. */
  restrictionDisplayNamesPath?: (string | number)[];
  /** Mirrors labels into result_params for banner deeplink suggestion names. */
  resultParamsPath?: (string | number)[];
  form?: FormInstance;
  "data-testid"?: string;
};

/**
 * Multi-select searchable game picker for free-spin-eligible casino games.
 * Stores its value as a comma-separated string of game IDs to stay compatible
 * with the backend's `parse_id_list()`.
 */
const FreeSpinGameSelect: FC<FreeSpinGameSelectProps> = ({
  value,
  onChange,
  provider,
  disabled = false,
  restrictionDisplayNamesPath,
  resultParamsPath,
  form,
  ...rest
}) => {
  const testId = rest["data-testid"] ?? "free-spin-game-select";
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // id → display label cache built from API responses
  const [labelCache, setLabelCache] = useState<Record<string, string>>({});

  const { effectiveSearchQuery } = useDebouncedSearchQuery(rawSearch);
  const isSearching = effectiveSearchQuery.trim().length > 0;

  // Fetch the full game list once per provider; search is done client-side.
  const gamesQuery = useGetFreeSpinGames(
    { provider: provider || undefined },
    !disabled && Boolean(provider),
  );
  const isLoadingGames =
    gamesQuery.isLoading || (gamesQuery.isFetching && !gamesQuery.data);

  const allGames = useMemo(() => {
    const games = gamesQuery.data ?? [];
    if (!isSearching || !open) return games;
    const q = effectiveSearchQuery.toLowerCase();
    return games.filter(
      (g) =>
        g.game_name.toLowerCase().includes(q) ||
        g.game_id.toLowerCase().includes(q),
    );
  }, [gamesQuery.data, isSearching, open, effectiveSearchQuery]);

  // Build label cache from every API response
  useEffect(() => {
    if (allGames.length === 0) return;
    setLabelCache((prev) => {
      let next = prev;
      for (const game of allGames) {
        rememberRestrictionDisplayName(game.game_id, game.game_name);
        if (!next[game.game_id]) {
          if (next === prev) next = { ...prev };
          next[game.game_id] = gameLabel(game);
        }
      }
      return next;
    });
  }, [allGames]);

  const selectedIds = useMemo(() => parseCSV(value), [value]);

  const chips: GameChip[] = useMemo(
    () =>
      selectedIds.map((id) => ({
        id,
        label: labelCache[id] ?? id,
      })),
    [selectedIds, labelCache],
  );

  // Client-side paginate the results (API returns all at once)
  const visible = allGames.slice(0, visibleCount);
  const hasMore = allGames.length > visibleCount;

  const closePanel = useCallback(() => {
    setOpen(false);
    setRawSearch("");
    setVisibleCount(PAGE_SIZE);
  }, []);

  const syncRestrictionLabels = useCallback(
    (ids: string[], cache: Record<string, string>) => {
      if (!form) return;
      const labels = buildRestrictionLabels(ids, cache);
      if (restrictionDisplayNamesPath) {
        form.setFieldValue(restrictionDisplayNamesPath, labels);
      }
      if (resultParamsPath) {
        const current =
          (form.getFieldValue(resultParamsPath) as Record<string, unknown>) ??
          {};
        form.setFieldValue(resultParamsPath, {
          ...current,
          [RESTRICTION_DISPLAY_NAMES_PARAM_KEY]: labels,
        });
      }
    },
    [form, restrictionDisplayNamesPath, resultParamsPath],
  );

  useEffect(() => {
    syncRestrictionLabels(selectedIds, labelCache);
  }, [selectedIds, labelCache, syncRestrictionLabels]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closePanel();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, closePanel]);

  // Reset games panel when provider changes
  useEffect(() => {
    closePanel();
  }, [provider, closePanel]);

  const addGame = (id: string, label?: string) => {
    if (selectedIds.includes(id)) return;
    const next = [...selectedIds, id];
    const nextCache = label
      ? { ...labelCache, [id]: label }
      : labelCache;
    if (label) {
      setLabelCache(nextCache);
    }
    onChange?.(toCSV(next));
    syncRestrictionLabels(next, nextCache);
  };

  const removeGame = (id: string) => {
    const next = selectedIds.filter((gid) => gid !== id);
    onChange?.(toCSV(next));
    syncRestrictionLabels(next, labelCache);
  };

  const handleSelectGame = (game: FreeSpinGame) => {
    rememberRestrictionDisplayName(game.game_id, game.game_name);
    if (selectedIds.includes(game.game_id)) {
      removeGame(game.game_id);
    } else {
      addGame(game.game_id, gameLabel(game));
    }
  };

  const handleManualEntry = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const raw = rawSearch.trim();
    if (!raw) return;
    addGame(raw);
    setRawSearch("");
  };

  const handleOpenPanel = () => {
    if (disabled || !provider) return;
    setOpen(true);
    setVisibleCount(PAGE_SIZE);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className={styles.root} ref={rootRef} data-testid={testId}>
      {/* Selected game chips */}
      {chips.length > 0 && (
        <div className={styles.chips} data-testid={`${testId}-chips`}>
          {chips.map((chip) => (
            <span
              key={chip.id}
              className={styles.chip}
              data-testid={`${testId}-chip-${chip.id}`}
            >
              <span className={styles.chipLabel}>{chip.label}</span>
              {!disabled && (
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => removeGame(chip.id)}
                  aria-label={`Remove ${chip.label}`}
                  data-testid={`${testId}-chip-remove-${chip.id}`}
                >
                  {"\u00d7"}
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* "+ Game ID" trigger or inline search */}
      {open && !disabled ? (
        <div className={styles.inlineSearchWrap}>
          <div className={`${styles.triggerWrap} ${styles.triggerOpen}`}>
            <input
              ref={inputRef}
              type="text"
              className={styles.trigger}
              value={rawSearch}
              placeholder={
                provider
                  ? "Search games or enter a Game ID…"
                  : "Select a provider first"
              }
              onChange={(e) => {
                setRawSearch(e.target.value);
                setVisibleCount(PAGE_SIZE);
              }}
              onKeyDown={handleManualEntry}
              disabled={!provider}
              data-testid={`${testId}-search`}
            />
            <button
              type="button"
              className={styles.chevron}
              onClick={closePanel}
              tabIndex={-1}
              aria-label="Close game list"
              data-testid={`${testId}-close`}
            >
              {"\u2303"}
            </button>
          </div>

          {provider && (
            <div
              className={styles.panel}
              role="listbox"
              aria-label="Free spin games"
              data-testid={`${testId}-panel`}
            >
              {isLoadingGames && !gamesQuery.isError ? (
                <div className={styles.statusRow} data-testid={`${testId}-loading`}>
                  Loading games…
                </div>
              ) : gamesQuery.isError ? (
                <div className={styles.statusRow} data-testid={`${testId}-error`}>
                  <span>
                    {gamesQuery.error?.message?.trim() || "Failed to load games"}
                  </span>
                  <button
                    type="button"
                    className={styles.retryButton}
                    onClick={() => gamesQuery.refetch()}
                    data-testid={`${testId}-retry`}
                  >
                    Retry
                  </button>
                </div>
              ) : visible.length === 0 ? (
                <div className={styles.statusRow} data-testid={`${testId}-empty`}>
                  {isSearching
                    ? "No matching games — press Enter to add as raw ID"
                    : "No games available for this provider"}
                </div>
              ) : (
                <div className={styles.optionList}>
                  {visible.map((game) => {
                    const isSelected = selectedIds.includes(game.game_id);
                    return (
                      <button
                        key={game.game_id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`${styles.optionRow} ${isSelected ? styles.optionSelected : ""}`}
                        onClick={() => handleSelectGame(game)}
                        title={gameLabel(game)}
                        data-testid={`${testId}-option-${game.game_id}`}
                      >
                        <span className={styles.optionName}>{game.game_name}</span>
                        <span className={styles.optionId}>({game.game_id})</span>
                      </button>
                    );
                  })}
                  {!isSearching && allGames.length > 0 && (
                    <div
                      className={styles.statusRow}
                      data-testid={`${testId}-count`}
                    >
                      {allGames.length} game{allGames.length === 1 ? "" : "s"} available
                    </div>
                  )}
                  {hasMore && (
                    <button
                      type="button"
                      className={styles.showMore}
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      data-testid={`${testId}-show-more`}
                    >
                      Show more ({allGames.length - visibleCount} remaining)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          className={styles.addButton}
          onClick={handleOpenPanel}
          disabled={disabled || !provider}
          data-testid={`${testId}-add`}
        >
          + Game ID
        </button>
      )}
    </div>
  );
};

export default FreeSpinGameSelect;
